/**
 * The "hooray" played with the confetti when a sign-up arrives. Synthesised
 * with Web Audio so there is no asset to load: a cannon "pop", then a rising
 * four-note fanfare that lands on a chord.
 *
 * Browsers only let a page make sound after the user has clicked/typed on it,
 * so the AudioContext has to be unlocked from a user gesture (`unlockSound`).
 */

const PREF_KEY = 'growth.sound';

/** On unless the user explicitly turned it off. */
export const soundPreferred = (stored: string | null): boolean =>
  stored !== 'off';

export const readSoundPref = (): boolean => {
  try {
    return soundPreferred(window.localStorage.getItem(PREF_KEY));
  } catch {
    return true;
  }
};

export const writeSoundPref = (on: boolean): void => {
  try {
    window.localStorage.setItem(PREF_KEY, on ? 'on' : 'off');
  } catch {
    /* private mode etc. */
  }
};

/** [frequency Hz, start offset s, duration s] */
export const FANFARE: ReadonlyArray<readonly [number, number, number]> = [
  [523.25, 0.0, 0.14], // C5
  [659.25, 0.14, 0.14], // E5
  [783.99, 0.28, 0.14], // G5
  [1046.5, 0.42, 0.9], // C6, held
  [659.25, 0.42, 0.9], // E5 under it
  [783.99, 0.42, 0.9], // G5 under it
];

export const FANFARE_LENGTH_S = Math.max(...FANFARE.map(([, t, d]) => t + d));

type AudioContextCtor = new () => AudioContext;

let ctx: AudioContext | null | undefined;
const getCtx = (): AudioContext | null => {
  if (ctx !== undefined) return ctx;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  let created: AudioContext | null = null;
  try {
    created = Ctor ? new Ctor() : null;
  } catch {
    created = null;
  }
  ctx = created;
  return created;
};

export const soundSupported = (): boolean => getCtx() !== null;

export const soundReady = (): boolean => getCtx()?.state === 'running';

/** Call from a user gesture (click/keydown). Resolves true once audible. */
export const unlockSound = async (): Promise<boolean> => {
  const c = getCtx();
  if (!c) return false;
  if (c.state !== 'running') {
    try {
      await c.resume();
    } catch {
      return false;
    }
  }
  return c.state === 'running';
};

const pop = (c: BaseAudioContext, out: AudioNode, at: number) => {
  const len = Math.floor(c.sampleRate * 0.25);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 900;
  filter.Q.value = 0.7;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.5, at);
  gain.gain.exponentialRampToValueAtTime(0.001, at + 0.25);
  src.connect(filter).connect(gain).connect(out);
  src.start(at);
};

const note = (
  c: BaseAudioContext,
  out: AudioNode,
  freq: number,
  at: number,
  dur: number,
) => {
  const osc = c.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = freq;
  const bright = c.createOscillator();
  bright.type = 'square';
  bright.frequency.value = freq;
  const brightGain = c.createGain();
  brightGain.gain.value = 0.08;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(0.35, at + 0.02);
  gain.gain.setValueAtTime(0.35, at + Math.max(0.02, dur - 0.1));
  gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  osc.connect(gain);
  bright.connect(brightGain).connect(gain);
  gain.connect(out);
  osc.start(at);
  bright.start(at);
  osc.stop(at + dur + 0.05);
  bright.stop(at + dur + 0.05);
};

/** Schedules the whole hooray on `c` starting at `at` (context time). */
export const scheduleHooray = (c: BaseAudioContext, at: number): void => {
  const master = c.createGain();
  master.gain.value = 0.6;
  master.connect(c.destination);
  pop(c, master, at);
  for (const [freq, t, dur] of FANFARE) note(c, master, freq, at + t, dur);
};

/** Plays the hooray now if the browser lets us; returns whether it did. */
export const playHooray = (): boolean => {
  const c = getCtx();
  if (!c || c.state !== 'running') return false;
  scheduleHooray(c, c.currentTime + 0.02);
  return true;
};
